import {useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {useMutation, useQuery} from '@apollo/client';
import {Form, Input, InputNumber, Spin, Switch, TimePicker} from 'antd';
import moment from 'moment';
import {toast} from 'react-toastify';
import {Button} from '@dabafinance/react-components';
import shell from 'pages/AssetManagement/v2/pageShell.module.scss';
import {FormCard, spans, Hint} from 'pages/AssetManagement/v2/FormCard';
import {InstrumentActions} from 'pages/AssetManagement/v2/InstrumentActions';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import {FeeScheduleTable} from 'components/feeSchedule/FeeScheduleTable';
import {NavTabsEnum} from 'utils/constants';
import {makeSelectTabOperations} from 'redux/store/auth';
import {ExchangeScopeSelector} from './ExchangeScopeSelector';
import {INSTRUMENT_TYPES, useExchangeScope} from './useExchangeScope';
import {
  ExchangeInstrumentConfigForm,
  countOverrides,
  isRealOverride,
} from './ExchangeInstrumentConfigForm';
import {buildVenueBreakdownQuery, readVenueBreakdown} from './resolvers';
import {GET_EXCHANGE_TRADING_CONFIG, UPDATE_STOCK_EXCHANGE} from './resolvers';
import styles from './exchanges.module.scss';

/**
 * Cutoff and settlement times are stored as bare "H:mm" strings, unpadded —
 * BRVM holds "13:9". Parse leniently, write back padded.
 */
const parseTime = value => {
  if (!value) return null;
  const parsed = moment(value, ['HH:mm', 'H:m', 'HH:mm:ss'], true);
  return parsed.isValid() ? parsed : null;
};
const formatTime = value => (value ? moment(value).format('HH:mm') : null);

/**
 * Trading View → Exchange Trading Config.
 *
 * The operational half of an exchange: everything that changes order
 * behaviour for every instrument listed on it. It lives beside the orders it
 * affects rather than with the branding fields it used to be buried among.
 *
 * The update is a merge, so this screen sends only the trading fields and
 * leaves identity and localised content — owned by Multi Asset — untouched.
 * `baseCurrencyCode` is the exception: it is NON_NULL on the input, so it has
 * to be echoed back on every save even though nothing here edits it.
 */
export const ExchangeTradingConfig = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const scope = useExchangeScope();
  const {exchangeId} = scope;
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pending, setPending] = useState(null);
  // 'EXCHANGE' or an InstrumentKey.
  const [tab, setTab] = useState('EXCHANGE');
  // Draft overrides, keyed by instrument key. Seeded from the saved sparse
  // list; an entry only exists once something on that type is overridden.
  const [overrides, setOverrides] = useState({});

  const pageOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.StockListings),
  );

  const {data, loading, refetch} = useQuery(GET_EXCHANGE_TRADING_CONFIG, {
    variables: {id: [exchangeId]},
    skip: !exchangeId,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const node = (data?.getExchanges?.data ?? []).find(
    entry => entry.exchange?.id === exchangeId,
  );
  const exchange = node?.exchange;

  // Only render a tab for a type actually listed here. InstrumentKey has 23
  // values; a tab per key would be mostly meaningless for any given exchange.
  const breakdownQuery = useMemo(
    () =>
      buildVenueBreakdownQuery(
        exchangeId ? [exchangeId] : [],
        INSTRUMENT_TYPES,
      ),
    [exchangeId],
  );
  const {data: breakdownData} = useQuery(breakdownQuery, {
    skip: !exchangeId,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  const listedTypes = useMemo(() => {
    if (!breakdownData || !exchangeId) return [];
    const counts =
      readVenueBreakdown(breakdownData, [exchangeId], INSTRUMENT_TYPES)?.[
        exchangeId
      ] ?? {};
    return INSTRUMENT_TYPES.filter(type => (counts[type.key] ?? 0) > 0);
  }, [breakdownData, exchangeId]);

  // Exchanges list different things, so a tab that was valid on one of them can
  // be absent on the next — without this you keep editing Funds on an exchange
  // that lists none, under a tab bar with nothing selected.
  useEffect(() => {
    if (tab === 'EXCHANGE' || !listedTypes.length) return;
    if (!listedTypes.some(type => type.key === tab)) setTab('EXCHANGE');
  }, [listedTypes, tab]);

  const [updateExchange, {loading: saving}] = useMutation(
    UPDATE_STOCK_EXCHANGE,
    {errorPolicy: 'all'},
  );

  useEffect(() => {
    if (!exchange) return;
    form.setFieldsValue({
      tradeable: exchange.tradeable ?? false,
      fractionalShares: exchange.fractionalShares ?? false,
      enableLimitOrder: exchange.enableLimitOrder ?? false,
      enableMarketOrder: exchange.enableMarketOrder ?? false,
      orderExpirationLimit: exchange.orderExpirationLimit,
      orderProcessingCutoffTime: parseTime(exchange.orderProcessingCutoffTime),
      estimatedSettlementDays: exchange.estimatedSettlementDays,
      estimatedProcessingDays: exchange.estimatedProcessingDays,
      autoSettlement: exchange.autoSettlementConfig?.autoSettlement ?? false,
      daysAfterFill: exchange.autoSettlementConfig?.daysAfterFill,
      settlementTime: parseTime(exchange.autoSettlementConfig?.settlementTime),
      defaultClosureTitle: exchange.defaultClosureTitle,
      defaultClosureMessage: exchange.defaultClosureMessage,
      feeSchedule: (exchange.feeSchedule ?? []).map(fee => ({
        feeName: fee?.feeName,
        feeType: fee?.feeType,
        fixedFeeAmount: fee?.fixedFeeAmount,
        variableFeeAmount: fee?.variableFeeAmount,
        level: fee?.level,
        perExchange: fee?.perExchange,
      })),
    });
  }, [exchange]);

  useEffect(() => {
    if (!exchange) return;
    // The saved list is sparse and returns null for anything not overridden;
    // strip those so an untouched field stays "inherit" rather than becoming
    // an explicit null the admin never chose.
    const next = {};
    (exchange.instrumentConfigs ?? []).forEach(config => {
      const entry = {instrumentKey: config.instrumentKey};
      Object.entries(config).forEach(([field, value]) => {
        if (field === '__typename' || field === 'inheritedFields') return;
        if (field === 'instrumentKey') return;
        if (value === null || value === undefined) return;
        if (Array.isArray(value) && !value.length) return;
        entry[field] =
          field === 'feeSchedule'
            ? value.map(fee => ({
                feeName: fee.feeName,
                feeType: fee.feeType,
                fixedFeeAmount: fee.fixedFeeAmount,
                variableFeeAmount: fee.variableFeeAmount,
                ...(fee.level && {level: fee.level}),
              }))
            : value;
      });
      next[config.instrumentKey] = entry;
    });
    setOverrides(next);
  }, [exchange]);

  /**
   * The override payload.
   *
   * `instrumentConfigs` is overwritten wholesale on update, exactly like
   * `feeSchedule` — so this always sends the complete set. A type dropped from
   * the array has its override deleted, which is how "reset this whole type"
   * works. Entries carrying nothing but their key are dropped for that reason.
   */
  const buildInstrumentConfigs = () =>
    Object.values(overrides)
      .map(entry => {
        const cleaned = {instrumentKey: entry.instrumentKey};
        Object.entries(entry).forEach(([field, value]) => {
          // null means inherit, so it is simply omitted. false and 0 are real
          // overrides and must survive this filter.
          if (field === 'instrumentKey' || !isRealOverride(value)) return;
          cleaned[field] = value;
        });
        return cleaned;
      })
      .filter(entry => Object.keys(entry).length > 1);

  const buildInput = values => ({
    // Required by the input even though this screen never edits it.
    baseCurrencyCode: exchange.baseCurrencyCode,
    tradeable: values.tradeable ?? false,
    fractionalShares: values.fractionalShares ?? false,
    enableLimitOrder: values.enableLimitOrder ?? false,
    enableMarketOrder: values.enableMarketOrder ?? false,
    orderExpirationLimit: values.orderExpirationLimit ?? null,
    orderProcessingCutoffTime: formatTime(values.orderProcessingCutoffTime),
    estimatedSettlementDays: values.estimatedSettlementDays ?? null,
    estimatedProcessingDays: values.estimatedProcessingDays ?? null,
    defaultClosureTitle: values.defaultClosureTitle || null,
    defaultClosureMessage: values.defaultClosureMessage || null,
    autoSettlementConfig: {
      autoSettlement: values.autoSettlement ?? false,
      daysAfterFill: values.daysAfterFill ?? null,
      settlementTime: formatTime(values.settlementTime),
    },
    feeSchedule: (values.feeSchedule ?? [])
      .filter(fee => fee?.feeName)
      .map(fee => ({
        feeName: fee.feeName,
        feeType: fee.feeType,
        fixedFeeAmount: Number(fee.fixedFeeAmount ?? 0),
        variableFeeAmount: Number(fee.variableFeeAmount ?? 0),
        ...(fee.level && {level: fee.level}),
        // Only entry fees carry it, and it must be sent explicitly: the whole
        // schedule is overwritten on save, so an omitted false would read as
        // "unchanged" against a previously-true value.
        ...(fee.feeType === 'ENTRY' && {
          perExchange: Boolean(fee.perExchange),
        }),
      })),
    instrumentConfigs: buildInstrumentConfigs(),
  });

  const save = input =>
    updateExchange({variables: {id: exchange.id, input}})
      .then(({data: result}) => {
        const payload = result?.updateStockExchange;
        if (payload?.__typename === 'Error')
          return toast.error(payload.message);
        toast.success(`${exchange.code} trading settings saved`);
        setConfirmOpen(false);
        setPending(null);
        refetch();
      })
      .catch(error => toast.error(error.message));

  /**
   * What this save actually changes, field by field.
   *
   * Every save here rewrites order behaviour for every instrument on the
   * exchange, so the confirm shows a diff rather than a generic "are you
   * sure" — the point is to catch a switch nudged by accident.
   */
  const describeChanges = values => {
    const onOff = flag => (flag ? 'on' : 'off');
    const shown = value =>
      value === null || value === undefined || value === '' ? '—' : value;

    const rows = [];
    const compare = (
      label,
      before,
      after,
      format = shown,
      breaking = false,
    ) => {
      if (String(before ?? '') === String(after ?? '')) return;
      rows.push({label, from: format(before), to: format(after), breaking});
    };

    compare(
      'Tradeable',
      exchange.tradeable,
      values.tradeable,
      onOff,
      exchange.tradeable && !values.tradeable,
    );
    compare(
      'Market orders',
      exchange.enableMarketOrder,
      values.enableMarketOrder,
      onOff,
      exchange.enableMarketOrder && !values.enableMarketOrder,
    );
    compare(
      'Limit orders',
      exchange.enableLimitOrder,
      values.enableLimitOrder,
      onOff,
      exchange.enableLimitOrder && !values.enableLimitOrder,
    );
    compare(
      'Fractional shares',
      exchange.fractionalShares,
      values.fractionalShares,
      onOff,
    );
    compare(
      'Order expiry (days)',
      exchange.orderExpirationLimit,
      values.orderExpirationLimit,
    );
    compare(
      'Cutoff time',
      parseTime(exchange.orderProcessingCutoffTime)
        ? formatTime(parseTime(exchange.orderProcessingCutoffTime))
        : null,
      formatTime(values.orderProcessingCutoffTime),
    );
    compare(
      'Settles in (days)',
      exchange.estimatedSettlementDays,
      values.estimatedSettlementDays,
    );
    compare(
      'Processing (days)',
      exchange.estimatedProcessingDays,
      values.estimatedProcessingDays,
    );
    compare(
      'Auto-settle',
      exchange.autoSettlementConfig?.autoSettlement,
      values.autoSettlement,
      onOff,
    );
    compare(
      'Days after fill',
      exchange.autoSettlementConfig?.daysAfterFill,
      values.daysAfterFill,
    );
    compare(
      'Settlement time',
      parseTime(exchange.autoSettlementConfig?.settlementTime)
        ? formatTime(parseTime(exchange.autoSettlementConfig.settlementTime))
        : null,
      formatTime(values.settlementTime),
    );
    compare(
      'Closure title',
      exchange.defaultClosureTitle,
      values.defaultClosureTitle,
    );
    compare(
      'Closure message',
      exchange.defaultClosureMessage ? 'set' : null,
      values.defaultClosureMessage ? 'set' : null,
    );

    const feesBefore = (exchange.feeSchedule ?? []).length;
    const feesAfter = (values.feeSchedule ?? []).filter(
      fee => fee?.feeName,
    ).length;
    // Normalised so the comparison sees the same shape on both sides: the saved
    // value may be null where the form holds false, and a perExchange-only
    // toggle must still register as an edit.
    const feeFingerprint = f => [
      f.feeName,
      f.feeType,
      f.fixedFeeAmount,
      f.variableFeeAmount,
      f.level,
      Boolean(f.perExchange),
    ];
    const feesEdited =
      JSON.stringify((exchange.feeSchedule ?? []).map(feeFingerprint)) !==
      JSON.stringify(
        (values.feeSchedule ?? []).filter(f => f?.feeName).map(feeFingerprint),
      );
    if (feesEdited) {
      rows.push({
        label: 'Fee schedule',
        from: `${feesBefore} fee${feesBefore === 1 ? '' : 's'}`,
        to: `${feesAfter} fee${feesAfter === 1 ? '' : 's'}, edited`,
        breaking: false,
      });
    }
    // Per-type overrides are held outside the antd form, so the exchange diff
    // above cannot see them — without this a save from a type tab reported
    // "nothing has changed".
    const savedConfigs = JSON.stringify(
      (exchange.instrumentConfigs ?? [])
        .map(config => {
          const entry = {};
          Object.entries(config).forEach(([field, value]) => {
            if (field === '__typename' || field === 'inheritedFields') return;
            if (value === null || value === undefined) return;
            if (Array.isArray(value) && !value.length) return;
            entry[field] = value;
          });
          return entry;
        })
        .sort((a, b) => a.instrumentKey.localeCompare(b.instrumentKey)),
    );
    const nextConfigs = buildInstrumentConfigs().sort((a, b) =>
      a.instrumentKey.localeCompare(b.instrumentKey),
    );
    if (savedConfigs !== JSON.stringify(nextConfigs)) {
      const before = (exchange.instrumentConfigs ?? []).length;
      const after = nextConfigs.length;
      rows.push({
        label: 'Per-type overrides',
        from: `${before} type${before === 1 ? '' : 's'}`,
        to: `${after} type${after === 1 ? '' : 's'}`,
        // Dropping a type deletes its override outright.
        breaking: after < before,
        warning:
          'Removing a per-type override reverts that instrument type to the exchange defaults.',
      });
    }

    return rows;
  };

  const handleFinish = values => {
    if (!exchange.baseCurrencyCode) {
      return toast.error(
        'This exchange has no base currency, which the update requires. Set one on its Details screen first.',
      );
    }
    const changes = describeChanges(values);
    if (!changes.length) {
      return toast.info('Nothing has changed on this screen');
    }
    setPending({input: buildInput(values), changes});
    setConfirmOpen(true);
  };

  /**
   * What the type tabs show when a field is inherited.
   *
   * Read from the live form rather than the saved exchange: both halves go up
   * in one save, so an unsaved edit to the exchange defaults is what the type
   * tabs should be inheriting. Switching tabs re-renders this, which is the
   * only moment either half can have changed.
   */
  const liveCount = node?.instrumentCount ?? 0;

  /**
   * One line per distinct reason, not one per row. Turning off trading and
   * dropping a per-type override are both destructive but in different ways,
   * and the old single sentence claimed orders would stop for both.
   */
  const breakingWarnings = (pending?.changes ?? []).reduce((texts, row) => {
    if (!row.breaking) return texts;
    const text =
      row.warning ??
      'Orders will stop being accepted for the highlighted settings.';
    return texts.indexOf(text) === -1 ? [...texts, text] : texts;
  }, []);

  const header = (
    <div className={shell.head}>
      <div className={shell.headTop}>
        <div>
          <h1 className={shell.title}>Exchange Trading Config</h1>
          <div className={shell.sub}>
            Order behaviour for every instrument on the chosen exchange.
            {exchange
              ? ` ${liveCount.toLocaleString()} live instrument${
                  liveCount === 1 ? '' : 's'
                } affected.`
              : ''}
          </div>
        </div>
        {exchange && (
          <div className={shell.headActions}>
            <Button
              type="secondary"
              label="Closure windows"
              onClick={() =>
                navigate(
                  `/dashboard/stocks/exchange-closures?exchange=${exchange.id}`,
                )
              }
            />
          </div>
        )}
      </div>
      <ExchangeScopeSelector {...scope} />
    </div>
  );

  // Config is per exchange, so All markets has nothing to edit.
  if (!exchangeId) {
    return (
      <div>
        {header}
        <div className={`${shell.surface} ${styles.empty}`}>
          Choose an exchange above. Trading settings apply to one exchange at a
          time.
        </div>
      </div>
    );
  }

  if (loading && !data) {
    return (
      <div>
        {header}
        <div className="query-loading">
          <Spin size="large" />
          <h1>Loading…</h1>
        </div>
      </div>
    );
  }

  if (!exchange) {
    return (
      <div>
        {header}
        <div className={`${shell.surface} ${styles.empty}`}>
          That exchange could not be loaded.
        </div>
      </div>
    );
  }

  return (
    <div>
      {header}

      {/* Exchange defaults first, then one tab per instrument type listed here.
          The exchange form below is unchanged — the per-type tabs layer sparse
          overrides over it rather than replacing it. */}
      {listedTypes.length > 0 && (
        <nav className={styles.configTabs}>
          <button
            type="button"
            className={tab === 'EXCHANGE' ? styles.configTabOn : ''}
            onClick={() => setTab('EXCHANGE')}>
            Exchange defaults
          </button>
          {listedTypes.map(type => {
            const count = countOverrides(overrides[type.key]);
            return (
              <button
                key={type.key}
                type="button"
                className={tab === type.key ? styles.configTabOn : ''}
                onClick={() => setTab(type.key)}>
                {type.label}
                <span
                  className={`${styles.configTabCount} ${
                    count ? styles.configTabCountOn : ''
                  }`}>
                  {count || '—'}
                </span>
              </button>
            );
          })}
        </nav>
      )}

      <Form form={form} layout="vertical" onFinish={handleFinish}>
        {tab !== 'EXCHANGE' && (
          <ExchangeInstrumentConfigForm
            instrumentKey={tab}
            label={listedTypes.find(type => type.key === tab)?.label ?? tab}
            override={overrides[tab]}
            disabled={!pageOperations?.update}
            onChange={next =>
              setOverrides(previous => ({...previous, [tab]: next}))
            }
          />
        )}
        <div style={{display: tab === 'EXCHANGE' ? 'block' : 'none'}}>
          <FormCard
            title="Trading"
            description="Whether this exchange accepts orders at all, and of what kind">
            <div className={spans.third}>
              <Form.Item
                name="tradeable"
                label="Tradeable"
                valuePropName="checked">
                <Switch />
              </Form.Item>
              <Hint>Off, no orders are accepted on this exchange.</Hint>
            </div>
            <div className={spans.third}>
              <Form.Item
                name="enableMarketOrder"
                label="Market orders"
                valuePropName="checked">
                <Switch />
              </Form.Item>
            </div>
            <div className={spans.third}>
              <Form.Item
                name="enableLimitOrder"
                label="Limit orders"
                valuePropName="checked">
                <Switch />
              </Form.Item>
            </div>
            <div className={spans.third}>
              <Form.Item
                name="fractionalShares"
                label="Fractional shares"
                valuePropName="checked">
                <Switch />
              </Form.Item>
            </div>
            <div className={spans.third}>
              <Form.Item
                name="orderExpirationLimit"
                label="Order expiry (days)">
                <InputNumber className={styles.fullWidth} min={0} />
              </Form.Item>
            </div>
            <div className={spans.third}>
              <Form.Item name="orderProcessingCutoffTime" label="Cutoff time">
                <TimePicker className={styles.fullWidth} format="HH:mm" />
              </Form.Item>
              <Hint>
                Orders placed after this are processed the next session.
              </Hint>
            </div>
          </FormCard>

          <FormCard
            title="Settlement"
            description="What operations promise, and what the system does on its own">
            <div className={spans.third}>
              <Form.Item
                name="estimatedSettlementDays"
                label="Settles in (days)">
                <InputNumber className={styles.fullWidth} min={0} />
              </Form.Item>
            </div>
            <div className={spans.third}>
              <Form.Item
                name="estimatedProcessingDays"
                label="Processing (days)">
                <InputNumber className={styles.fullWidth} min={0} />
              </Form.Item>
            </div>
            <div className={spans.third}>
              <Form.Item
                name="autoSettlement"
                label="Auto-settle"
                valuePropName="checked">
                <Switch />
              </Form.Item>
            </div>
            <div className={spans.third}>
              <Form.Item name="daysAfterFill" label="Days after fill">
                <InputNumber className={styles.fullWidth} min={0} />
              </Form.Item>
              <Hint>Only used when auto-settle is on.</Hint>
            </div>
            <div className={spans.third}>
              <Form.Item name="settlementTime" label="Settlement time">
                <TimePicker className={styles.fullWidth} format="HH:mm" />
              </Form.Item>
            </div>
          </FormCard>

          <FormCard
            title="Fees"
            description="Charged on orders placed against this exchange">
            {/* The span goes on a wrapper, not on the Form.Item: FormCard's
                own `.cardBody > .ant-form-item { span 6 }` outranks `.full`,
                so a Form.Item asking for full width silently gets half.
                FeeScheduleTable stays the Form.Item's DIRECT child, which is
                what receives the injected value/onChange. */}
            <div className={spans.full}>
              <Form.Item
                name="feeSchedule"
                rules={[
                  {
                    // async so the rule rejects by throwing — the eslint env
                    // here has no es6 globals, so no bare Promise.reject.
                    validator: async (_, fees) => {
                      if (
                        !(fees ?? []).every(fee => fee?.feeName && fee?.feeType)
                      )
                        throw new Error('Every fee needs a name and a type');
                    },
                  },
                ]}>
                <FeeScheduleTable disabled={!pageOperations?.update} />
              </Form.Item>
            </div>
          </FormCard>

          <FormCard
            title="Closure messaging"
            description="Shown to investors when the market is shut. Closure windows themselves are scheduled separately.">
            <div className={spans.half}>
              <Form.Item name="defaultClosureTitle" label="Default title">
                <Input placeholder="Market closed" />
              </Form.Item>
            </div>
            <div className={spans.full}>
              <Form.Item name="defaultClosureMessage" label="Default message">
                <Input.TextArea
                  rows={3}
                  placeholder="The market is currently closed, but you can still place your order now."
                />
              </Form.Item>
              <Hint>
                Current state is{' '}
                {exchange.closure?.isMarketOpen ? 'open' : 'closed'} — that
                comes from the scheduled windows, not from this text.
              </Hint>
            </div>
          </FormCard>
        </div>

        {/* One save for the whole exchange, so it sits outside the tab
            wrapper — the exchange defaults and every per-type override go up in
            a single updateStockExchange call. */}
        <InstrumentActions
          disabled={!pageOperations?.update || saving}
          label={saving ? 'Saving…' : 'Save trading settings'}
        />
      </Form>

      <ConfirmModal
        openModal={confirmOpen}
        setOpenModal={() => setConfirmOpen(false)}
        loading={saving}
        title={`Save ${exchange.code} trading settings`}
        confirmText={
          <span>
            <span className={styles.diffIntro}>
              Applies to all <strong>{liveCount.toLocaleString()}</strong> live
              instrument
              {liveCount === 1 ? '' : 's'} on <strong>{exchange.code}</strong>{' '}
              immediately.
            </span>
            <span className={styles.diffList}>
              {(pending?.changes ?? []).map(row => (
                <span
                  key={row.label}
                  className={`${styles.diffRow} ${
                    row.breaking ? styles.diffRowBreaking : ''
                  }`}>
                  <span className={styles.diffLabel}>{row.label}</span>
                  <span className={styles.diffFrom}>{row.from}</span>
                  <span className={styles.diffArrow}>→</span>
                  <span className={styles.diffTo}>{row.to}</span>
                </span>
              ))}
            </span>
            {breakingWarnings.map(text => (
              <span key={text} className={styles.diffWarn}>
                {text}
              </span>
            ))}
          </span>
        }
        handleOperation={() => save(pending.input)}
      />
    </div>
  );
};

export default ExchangeTradingConfig;
